import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type MandatoryComplianceResponse,
  type MandatoryComplianceItem,
  type MandatoryComplianceParams,
} from "../../services/reports-extended.api";
import { Shield, Approve, Cancel, Employees } from "@/components/icons";

const PAGE_SIZE = 20;

const ComplianceReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [nonCompliantOnly, setNonCompliantOnly] = useState(false);
  const [page, setPage] = useState(1);

  const params: MandatoryComplianceParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: filter.search?.trim() || undefined,
      departmentID: filter.departmentID,
      nonCompliantOnly,
    }),
    [filter, nonCompliantOnly, page],
  );

  const { data, isLoading, error } = useReportData<
    MandatoryComplianceResponse,
    MandatoryComplianceParams
  >({
    fetcher: (p, signal) =>
      reportsExtendedApi.getMandatoryCompliance(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const byDepartment = data?.byDepartment ?? [];

  const columns: TableColumn<MandatoryComplianceItem>[] = [
    {
      key: "fullName",
      label: "KULLANICI",
      header: "KULLANICI",
      accessor: (row) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{row.fullName}</span>
          {row.email && (
            <span style={{ fontSize: 12, color: "#64748b" }}>{row.email}</span>
          )}
        </div>
      ),
    },
    {
      key: "department",
      label: t("reports.compliance.headerDepartment"),
      header: t("reports.compliance.headerDepartment"),
      accessor: (row) => row.department ?? "—",
    },
    {
      key: "requiredEducations",
      label: "ZORUNLU",
      header: "ZORUNLU",
      accessor: (row) => row.requiredEducations,
    },
    {
      key: "completedEducations",
      label: "TAMAMLANAN",
      header: "TAMAMLANAN",
      accessor: (row) => row.completedEducations,
    },
    {
      key: "missingEducations",
      label: t("reports.compliance.headerMissing"),
      header: t("reports.compliance.headerMissing"),
      accessor: (row) => (
        <span
          style={{
            color: row.missingEducations.length > 0 ? "#b91c1c" : "#15803d",
            fontWeight: 600,
          }}
          title={row.missingEducations
            .slice(0, 5)
            .map((m) => m.title)
            .join(", ")}
        >
          {row.missingEducations.length}
        </span>
      ),
    },
    {
      key: "isCompliant",
      label: "UYUM %",
      header: "UYUM %",
      accessor: (row) => (
        <span
          style={{
            fontWeight: 600,
            color: row.isCompliant
              ? "#15803d"
              : row.complianceRate >= 50
                ? "#b45309"
                : "#b91c1c",
          }}
        >
          {row.complianceRate.toFixed(0)}%
        </span>
      ),
    },
    {
      key: "isCompliant2",
      label: "DURUM",
      header: "DURUM",
      accessor: (row) =>
        row.isCompliant ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 10px",
              borderRadius: 999,
              background: "#dcfce7",
              color: "#166534",
              fontWeight: 600,
              fontSize: 11.5,
            }}
          >
            Uyumlu
          </span>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 10px",
              borderRadius: 999,
              background: "#fee2e2",
              color: "#b91c1c",
              fontWeight: 600,
              fontSize: 11.5,
            }}
          >
            Eksik
          </span>
        ),
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<MandatoryComplianceItem>({
    reportKey: "MANDATORY_COMPLIANCE",
    reportTitle: t("reports.compliance.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.compliance.title")}
      layoutKey={`report:MANDATORY_COMPLIANCE`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.compliance.title")}
      description={t("reports.compliance.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="MANDATORY_COMPLIANCE"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="zorunlu-egitim-compliance"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDateRange={false}
          extras={
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                color: "#475569",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={nonCompliantOnly}
                onChange={(e) => {
                  setNonCompliantOnly(e.target.checked);
                  setPage(1);
                }}
                style={{ accentColor: "#4f46e5" }}
              />
              <span>{t("reports.compliance.onlyMissingUsers")}</span>
            </label>
          }
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title={t("reports.compliance.totalUsers")}
          value={summary?.totalUsers ?? 0}
          icon={<Employees width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.compliance.kpiCompliant")}
          value={summary?.compliantUsers ?? 0}
          icon={<Approve width={18} height={18} />}
          accent="success"
          footnote={
            summary
              ? t("reports.compliance.kpiCompliantRate", {
                  rate: summary.overallComplianceRate.toFixed(1),
                })
              : undefined
          }
        />
        <ReportKPICard
          title={t("reports.compliance.kpiNonCompliant")}
          value={summary?.nonCompliantUsers ?? 0}
          icon={<Cancel width={18} height={18} />}
          accent="danger"
        />
        <ReportKPICard
          title={t("reports.compliance.mandatoryEducation")}
          value={summary?.mandatoryEducationCount ?? 0}
          icon={<Shield width={18} height={18} />}
        />
      </div>

      <ReportChart
        title={t("reports.compliance.deptComplianceRate")}
        subtitle={t("reports.compliance.deptComplianceSubtitle")}
        type="bar"
        isLoading={isLoading}
        isEmpty={byDepartment.length === 0}
        series={[
          {
            name: "Uyum %",
            data: byDepartment.slice(0, 15).map((d) => d.complianceRate),
          },
        ]}
        options={{
          xaxis: { categories: byDepartment.slice(0, 15).map((d) => d.name) },
          plotOptions: {
            bar: {
              horizontal: true,
              borderRadius: 4,
              barHeight: "70%",
              colors: {
                ranges: [
                  { from: 0, to: 50, color: "#b91c1c" },
                  { from: 50, to: 80, color: "#b45309" },
                  { from: 80, to: 100, color: "#15803d" },
                ],
              },
            },
          },
          yaxis: { max: 100 },
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.userID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("reports.compliance.noData") : t("reports.compliance.noUser")}
      />
    </ReportDetailLayout>
  );
};

export default ComplianceReportPage;
